// AI summary card at the top of a site's Overview: a few sentences on what changed and why,
// written by the Worker from this period's numbers (dashboard/src/summary.ts). Hidden when the
// Worker has no ANTHROPIC_API_KEY, on org-level views, and on share links that have no cached
// summary for the range. Admins get a refresh button; share links are read-only.
import type { SummaryResponse } from "../src/summary-types";
import { apiUrl, shared } from "./share";

type Api = <T>(path: string, p?: Record<string, string>) => Promise<T>;
export interface SummaryOpts {
  api: Api;
  /** The screen's params; only project, from and to are sent (summaries cover the whole site). */
  params: Record<string, string>;
  current: () => boolean;
  admin: boolean;
  /** True when dashboard filters are on: the summary doesn't follow them, so the card says so. */
  filtered: boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const POLL_MS = 4000;
const POLLS = 8;
/** Learned from the first answer: when the Worker isn't set up, stop drawing the card at all. */
let configured = true;

/** The card shell, with a slot `renderSummary` fills. Empty for org views or once we know it's off. */
export function summaryCard(isSite: boolean): string {
  if (!configured || !isSite) return "";
  return `<section class="card summary" id="summary" aria-live="polite" aria-busy="true">
    <div class="card-head"><h2>Summary</h2><span class="spacer"></span><span id="summary-act"></span></div>
    <div id="summary-body"><div class="skeleton" style="height:14px"></div><div class="skeleton" style="height:14px;margin-top:8px"></div><div class="skeleton" style="height:14px;margin-top:8px;width:60%"></div></div>
  </section>`;
}

const range = (p: Record<string, string>) => ({ project: p.project ?? "", from: p.from ?? "", to: p.to ?? "" });

export async function renderSummary(o: SummaryOpts, refresh = false): Promise<void> {
  const card = document.getElementById("summary");
  const body = document.getElementById("summary-body");
  const act = document.getElementById("summary-act");
  if (!card || !body || !act) return;
  const hide = () => card.remove();
  card.setAttribute("aria-busy", "true");
  try {
    let r: SummaryResponse;
    if (refresh) r = await post(range(o.params));
    else r = await o.api<SummaryResponse>("summary", range(o.params));
    for (let i = 0; r.status === "pending" && i < POLLS; i++) {
      await new Promise((res) => setTimeout(res, POLL_MS));
      if (!o.current()) return;
      r = await o.api<SummaryResponse>("summary", range(o.params));
    }
    if (!o.current()) return;
    if (r.status === "not_configured") configured = false;
    if (r.status !== "ready") return hide();
    body.innerHTML = `<p class="summary-text">${esc(r.text)}</p>
      <p class="summary-note">Written by AI from this period's numbers${o.filtered ? ". It covers the whole site; filters don't apply" : ""}. Check the charts below before acting on it.</p>`;
    act.innerHTML = o.admin && !shared ? `<button class="link" data-summary-refresh title="Write it again from the latest numbers">Refresh</button>` : "";
    card.setAttribute("aria-busy", "false");
  } catch (err) {
    if (!o.current()) return;
    // Staff see what went wrong; everyone else just doesn't get the card.
    if (!o.admin || shared) return hide();
    body.innerHTML = `<p class="summary-note">Couldn't write a summary right now (${esc((err as Error).message)}).</p>`;
    act.innerHTML = `<button class="link" data-summary-refresh>Try again</button>`;
    card.setAttribute("aria-busy", "false");
  }
}

async function post(p: { project: string; from: string; to: string }): Promise<SummaryResponse> {
  const res = await fetch(apiUrl("summary/refresh"), {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  return body as SummaryResponse;
}

/** The refresh button: show the skeleton again and ask for a new one. */
export function onSummaryRefresh(o: () => SummaryOpts): void {
  document.addEventListener("click", (ev) => {
    if (!(ev.target as HTMLElement).closest("[data-summary-refresh]")) return;
    const body = document.getElementById("summary-body");
    const act = document.getElementById("summary-act");
    if (act) act.innerHTML = `<span class="summary-note">Writing…</span>`;
    if (body) body.style.opacity = ".5";
    void renderSummary(o(), true).finally(() => body && (body.style.opacity = ""));
  });
}
